// shiba.js — a curious Shiba Inu pup living at the house.
//
// One animated shiba-inu.glb dog tied to the House backdrop (the way the deer are
// tied to the distant rings): environment.js calls setShibaActive(true) from
// setHouseVisible whenever the house is showing, and hides the pup when it's off.
//
// Behaviour: the pup starts sitting on the house deck (the raised engawa) for a
// good while, taking in the garden. Then it hops down off the front edge of the
// deck and roams the yard in front of the house — ambling, pausing to look around
// (idle), the odd short dash, and occasionally sitting down to watch again — a
// cute, curious pup. It stays in the strip in front of the house, clear of both
// the zen garden and the house footprint.
//
// Built lazily on first activation (the GLB loads once). Per-frame work is
// updateShiba(dt), called from main.js's animate().
//
// All randomness here is cosmetic (Math.random), never the seeded tree stream —
// the pup wandering must not perturb reproducible tree geometry.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { app } from './core.js';
import { nearestDeer, spookDeerHerd } from './deer.js';

const loader = new GLTFLoader();

// Clip names ship namespaced in the GLB. SitDown is the full sit-and-hold pose,
// Jump the leap, the rest are loops.
const CLIPS = ['Dog|Idle', 'Dog|Jump', 'Dog|Run', 'Dog|SitDown', 'Dog|Walk'];
const IDLE = 'Dog|Idle', JUMP = 'Dog|Jump', RUN = 'Dog|Run', SIT = 'Dog|SitDown', WALK = 'Dog|Walk';

// The GLB stores its coat colours in KHR_materials_pbrSpecularGlossiness diffuseFactors,
// an extension three's GLTFLoader (0.166) no longer parses — so the materials come back
// as default metallic grey and the orange coat is lost. We re-apply each colour by
// material name (the diffuseFactors, which are LINEAR-space rgb). See restoreCoat().
const COAT = {
  rusty: [0.799103, 0.132868, 0.0231534],   // the orange coat
  white: [0.8, 0.8, 0.8],                    // chest/legs/face
  blend: [0.799103, 0.327463, 0.18631],      // coat→white transition
  black: [0.0146688, 0.0146688, 0.0146688],  // nose/eyes
  pink: [0.810369951, 0.1352074031, 0.4292039283], // tongue/inner ear
};

// ── World geometry (mirrors the House placement in environment.js) ───────────
// The house GLB is scaled to height 365 and seated at (766, base=-58, 360),
// rotated to face the garden. Measuring its "floor" node through that transform
// puts the deck surface at world-Y ~10 and its garden-facing front edge between
// these two corners; the yard ground (the house base) sits at y=-60. These are
// constants rather than read off the live house mesh so the pup can build and
// place itself independently of when the house GLB finishes loading.
const DECK_Y = 4;      // world-Y of the deck surface the pup sits on (measured top sat a touch high)
const GROUND_Y = -30;    // world-Y of the visible ground/yard the pup lands on and roams
const EDGE_A = { x: 539, z: 131 }; // deck front (garden-facing) edge, corner A
const EDGE_B = { x: 584, z: 629 }; //                                  corner B

const SIT_SPOT = { x: 581, z: 378 };  // on the deck, just inside the front lip (toward the garden)
const LAND_SPOT = { x: 505, z: 345 }; // where the pup touches down after hopping off, out in the yard

// ── Play yard ────────────────────────────────────────────────────────────────
// The pup roams the open ground in front of and beside the house. Targets are
// sampled inside this generous box, then two carve-outs reject anything that
// would put it on the deck or in the zen garden, so the usable yard is the
// L-shaped strip in front of the deck plus the open ground off the garden's
// near corner — bigger than a thin lip, but never overlapping either structure.
const YARD = { xMin: 330, xMax: 545, zMin: 95, zMax: 675 };
// Zen-garden footprint (~900x980 → half 450x490) plus a margin: forbidden ground.
const GARDEN = { x: 470, z: 510 }; // inside == |x| < GARDEN.x && |z| < GARDEN.z
// Keep the pup at least this far in front of the deck's garden-facing edge so its
// (now larger) body never pokes through the deck. EDGE_N is that edge's unit
// normal pointing toward the garden (the yard side).
const DECK_CLEAR = 32;
const EDGE_N = { x: -0.9959, z: 0.0899 };

const GARDEN_RECT = { xMin: -GARDEN.x, xMax: GARDEN.x, zMin: -GARDEN.z, zMax: GARDEN.z };
// Detour waypoint just outside the garden's near (+x,+z) corner. The L-shaped yard
// wraps that corner, so a target in the far arm is reached by walking AROUND it
// here rather than pressing straight into the garden wall (see setTravel).
const GARDEN_CORNER = { x: GARDEN.x + 14, z: GARDEN.z + 14 };

function inGarden(x, z) { return Math.abs(x) < GARDEN.x && Math.abs(z) < GARDEN.z; }
// Signed distance from the deck edge along EDGE_N (positive = out in the yard).
function frontOfDeck(x, z) { return (x - EDGE_A.x) * EDGE_N.x + (z - EDGE_A.z) * EDGE_N.z; }
function inYard(x, z) { return !inGarden(x, z) && frontOfDeck(x, z) >= DECK_CLEAR; }

const WALK_SPEED = 34;   // world units/sec — an amble across the yard
const RUN_SPEED = 96;    // a short happy dash
const TURN_RATE = 3.0;   // rad/sec yaw rate
const ARRIVE = 10;       // distance to target that counts as arrived

const JUMP_DUR = 0.85;   // seconds the hop-down arc takes
const JUMP_RISE = 16;    // extra height of the little up-arc at the start of the hop

// Deer-chasing: the herd stays out in its meadow ring (it never comes to him), so
// every so often the pup bolts OUT of the yard toward the herd, gets close enough
// to scare them, then trots home. He never follows into the forest — the run is
// capped by distance and time — and he only sets off when the straight path to the
// deer doesn't cross the house or the zen garden (so he never barrels through them).
const CHASE_CHANCE = 0.2;   // odds, at each roam decision, he sets off after the deer
const SPOOK_DIST = 380;     // the skittish deer bolt once he's charged this close
const CHASE_SPEED = 165;    // an excited sprint (faster than a happy dash)
const CHASE_LIMIT_R = 1180; // max distance from the garden centre — well short of the forest ring
const CHASE_MAX_T = 9;      // seconds before he tires and trots home regardless
const CHASE_COOLDOWN = 14;  // seconds of normal roaming before he'll give chase again

// House footprint (a bit bigger than the GLB's AABB) — a chase path that would
// cross this, or the GARDEN rect, is skipped so the pup never runs through either.
const HOUSE_RECT = { xMin: 495, xMax: 1030, zMin: 110, zMax: 645 };

let _chaseCooldown = 0;     // counts down after a chase

// The GLB dog faces +Z in its own space, so heading = atan2(dir.x, dir.z) aims it
// down its travel direction (same convention as the deer). If it ever walks
// tail-first, flip this to Math.PI.
const FACING_YAW = 0;

const SHIBA_FILE = 'assets/models/shiba-inu.glb';
const SHIBA_HEIGHT = 52; // a dog next to the 365-tall house

let shibaGroup = null; // parent; .visible mirrors _active
let pup = null;        // the single pup's state object
let _active = false;   // the house is showing → pup is live
let _building = false; // guard against double-loading on rapid toggles

function rand(a, b) { return a + Math.random() * (b - a); }

// Turn `cur` toward `goal` by at most `step` radians, on the shortest arc.
function turnToward(cur, goal, step) {
  let d = goal - cur;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  if (Math.abs(d) <= step) return goal;
  return cur + Math.sign(d) * step;
}

// Aim the pup down a heading toward (tx,tz) from its current position.
function faceToward(tx, tz) {
  const p = pup.root.position;
  pup.root.rotation.y = Math.atan2(tx - p.x, tz - p.z) + FACING_YAW;
}

// Crossfade to a clip. Default is a continuous loop; `once` plays through and holds
// its last frame (Jump); `repeats` > 1 plays the clip that many times then holds
// (the sit pose, so the pup visibly settles over a few sit cycles).
function play(name, fade = 0.3, once = false, repeats = 1) {
  const next = pup.actions[name];
  if (!next || pup.current === next) return;
  next.reset();
  next.enabled = true;
  next.setEffectiveWeight(1);
  if (once && repeats === 1) {
    next.clampWhenFinished = true;
    next.setLoop(THREE.LoopOnce, 1);          // play once, hold last frame (the leap)
  } else if (repeats > 1) {
    next.clampWhenFinished = true;
    next.setLoop(THREE.LoopRepeat, repeats);  // a few cycles, then hold (settling into a sit)
  } else {
    next.clampWhenFinished = false;
    next.setLoop(THREE.LoopRepeat, Infinity); // continuous loop (walk/run/idle)
  }
  next.play();
  if (pup.current) next.crossFadeFrom(pup.current, fade, false);
  pup.current = next;
  pup.currentName = name;
}

// Length of one playthrough of a clip (seconds), 0 if missing.
function clipDur(name) {
  const a = pup.actions[name];
  return a ? a.getClip().duration : 0;
}

// ── State machine ────────────────────────────────────────────────────────────
// deckSit: sit on the deck and take in the garden. jumpOff: hop down off the
// front edge to the yard. idle: stand and look around. walk: amble to a yard
// point. run: a short dash to a yard point. yardSit: sit in the yard and watch.

// A roam target somewhere in the play yard: rejection-sample the YARD box until a
// point lands in the usable area (out of the garden, clear in front of the deck).
function yardPoint() {
  for (let i = 0; i < 24; i++) {
    const x = rand(YARD.xMin, YARD.xMax);
    const z = rand(YARD.zMin, YARD.zMax);
    if (inYard(x, z)) return { x, z };
  }
  return { x: LAND_SPOT.x, z: LAND_SPOT.z }; // always-valid fallback
}

function enterDeckSit() {
  pup.state = 'deckSit';
  // Sit through two or three full SitDown cycles before hopping off; fall back to a
  // fixed spell if the clip length is somehow unavailable.
  const repeats = Math.random() < 0.5 ? 2 : 3;
  const dur = clipDur(SIT);
  pup.timer = dur > 0 ? dur * repeats : rand(16, 30);
  play(SIT, 0.4, false, repeats);
}

function enterJumpOff() {
  pup.state = 'jumpOff';
  pup.jumpT = 0;
  pup.jumpFrom = { x: pup.root.position.x, z: pup.root.position.z, y: pup.root.position.y };
  pup.jumpTo = { x: LAND_SPOT.x, z: LAND_SPOT.z, y: GROUND_Y };
  faceToward(LAND_SPOT.x, LAND_SPOT.z); // leap forward, off the deck
  play(JUMP, 0.15, true);
}

function enterIdle() {
  pup.state = 'idle';
  pup.timer = rand(2, 5);
  play(IDLE, 0.3);
}

function enterWalk() {
  pup.state = 'walk';
  setTravel(yardPoint());
  play(WALK, 0.3);
}

function enterRun() {
  pup.state = 'run';
  setTravel(yardPoint());
  play(RUN, 0.25);
}

function enterYardSit() {
  pup.state = 'yardSit';
  pup.timer = rand(5, 10);
  play(SIT, 0.35, true);
}

// After a pause, pick what the curious pup does next: maybe tear off after the
// deer (if a herd is grazing within reach and off cooldown); otherwise mostly
// amble on, sometimes a short dash, sometimes settle down and watch.
function roamNext() {
  if (_chaseCooldown <= 0 && Math.random() < CHASE_CHANCE && tryChase()) return;
  const r = Math.random();
  if (r < 0.58) enterWalk();
  else if (r < 0.78) enterRun();
  else enterYardSit();
}

// True if the segment a→b stays out of a rect (sampled along its length).
function segMissesRect(ax, az, bx, bz, r) {
  for (let i = 0; i <= 10; i++) {
    const t = i / 10, x = ax + (bx - ax) * t, z = az + (bz - az) * t;
    if (x > r.xMin && x < r.xMax && z > r.zMin && z < r.zMax) return false;
  }
  return true;
}

// Set off after the herd only if there's a grazing deer and the straight run to it
// clears both the house and the zen garden. Returns true if a chase started.
function tryChase() {
  const p = pup.root.position;
  const deer = nearestDeer(p.x, p.z);
  if (!deer) return false;
  if (!segMissesRect(p.x, p.z, deer.x, deer.z, HOUSE_RECT)) return false;
  if (!segMissesRect(p.x, p.z, deer.x, deer.z, GARDEN_RECT)) return false;
  enterChase();
  return true;
}

function enterChase() {
  pup.state = 'chase';
  pup.timer = CHASE_MAX_T;
  play(RUN, 0.2);
}

function enterReturn() {
  pup.state = 'return';
  setTravel(yardPoint());
  _chaseCooldown = CHASE_COOLDOWN;
  play(RUN, 0.2);
}

// Move toward pup.target at `speed`, steering yaw to face travel, holding the
// yard ground height. Returns true on arrival.
function moveToward(speed, dt) {
  const p = pup.root.position;
  const dx = pup.target.x - p.x;
  const dz = pup.target.z - p.z;
  const dist = Math.hypot(dx, dz);
  if (dist < ARRIVE) return true;

  pup.root.rotation.y = turnToward(pup.root.rotation.y, Math.atan2(dx, dz) + FACING_YAW, TURN_RATE * dt);
  const step = Math.min(speed * dt, dist);
  p.x += (dx / dist) * step;
  p.z += (dz / dist) * step;
  // Backstop: should a step still land inside the garden, push it back out along the
  // shallower axis so the pup never ends up standing in the garden. (Normal travel
  // routes around the garden via setTravel, so this rarely fires.)
  if (inGarden(p.x, p.z)) {
    if (GARDEN.x - Math.abs(p.x) < GARDEN.z - Math.abs(p.z)) p.x = Math.sign(p.x || 1) * GARDEN.x;
    else p.z = Math.sign(p.z || 1) * GARDEN.z;
  }
  p.y = GROUND_Y;
  return false;
}

// Aim the pup at `dst`. If the straight path would cut through the garden, send it
// to the garden's near corner first, so it visibly walks AROUND the garden instead
// of pressing into the wall (which left it treadmilling in place). pup.via holds
// the corner detour, pup.dest the final destination.
function setTravel(dst) {
  const p = pup.root.position;
  pup.dest = dst;
  pup.via = segMissesRect(p.x, p.z, dst.x, dst.z, GARDEN_RECT) ? null : GARDEN_CORNER;
  pup.target = pup.via || dst;
}

// Advance one travel leg; on reaching the corner detour, continue to the final
// destination. Returns true only once the destination itself is reached.
function travel(speed, dt) {
  if (!moveToward(speed, dt)) return false;
  if (pup.via) { pup.via = null; pup.target = pup.dest; return false; }
  return true;
}

function tick(dt) {
  pup.mixer.update(dt);

  if (_chaseCooldown > 0) _chaseCooldown -= dt; // cools down between deer chases (see roamNext)

  switch (pup.state) {
    case 'deckSit':
      pup.timer -= dt;
      if (pup.timer <= 0) enterJumpOff();
      break;

    case 'jumpOff': {
      pup.jumpT = Math.min(pup.jumpT + dt / JUMP_DUR, 1);
      const p = pup.jumpT;
      const pos = pup.root.position;
      pos.x = pup.jumpFrom.x + (pup.jumpTo.x - pup.jumpFrom.x) * p;
      pos.z = pup.jumpFrom.z + (pup.jumpTo.z - pup.jumpFrom.z) * p;
      // Linear descent plus a small up-arc so it reads as a hop, not a slide.
      pos.y = pup.jumpFrom.y + (pup.jumpTo.y - pup.jumpFrom.y) * p + JUMP_RISE * Math.sin(Math.PI * p);
      if (pup.jumpT >= 1) { pos.y = GROUND_Y; enterIdle(); }
      break;
    }

    case 'idle':
      pup.timer -= dt;
      if (pup.timer <= 0) roamNext();
      break;

    case 'walk':
      if (travel(WALK_SPEED, dt)) enterIdle();
      break;

    case 'run':
      if (travel(RUN_SPEED, dt)) enterIdle();
      break;

    case 'yardSit':
      pup.timer -= dt;
      if (pup.timer <= 0) enterIdle();
      break;

    case 'chase': {
      const deer = nearestDeer(pup.root.position.x, pup.root.position.z);
      if (!deer) { enterReturn(); break; } // already scared off — head home
      pup.target = { x: deer.x, z: deer.z };
      const caught = moveToward(CHASE_SPEED, dt);
      const pos = pup.root.position;
      const gap = Math.hypot(deer.x - pos.x, deer.z - pos.z);
      pup.timer -= dt;
      // Close enough to scare them, or too far out / tired: bark them off and return.
      if (caught || gap < SPOOK_DIST || Math.hypot(pos.x, pos.z) > CHASE_LIMIT_R || pup.timer <= 0) {
        spookDeerHerd();
        enterReturn();
      }
      break;
    }

    case 'return':
      if (travel(RUN_SPEED, dt)) enterIdle();
      break;
  }
}

// Re-apply the coat colour the dropped spec-gloss extension left grey: match the
// material by name to its COAT diffuseFactor (linear rgb) and reset it to a matte,
// non-metallic fur look. Handles single or multi-material meshes.
function restoreCoat(material) {
  for (const m of Array.isArray(material) ? material : [material]) {
    const rgb = m && COAT[m.name];
    if (!rgb) continue;
    m.color.setRGB(rgb[0], rgb[1], rgb[2], THREE.LinearSRGBColorSpace);
    if ('metalness' in m) m.metalness = 0;       // fur, not metal (spec-gloss left it 1)
    if ('roughness' in m) m.roughness = 0.85;
    m.needsUpdate = true;
  }
}

// Build the single pup from the loaded GLB, scaled and seated so its feet rest on
// whatever surface its group sits on.
function buildPup(gltf) {
  const model = gltf.scene;

  const size = new THREE.Vector3();
  new THREE.Box3().setFromObject(model).getSize(size);
  model.scale.setScalar(SHIBA_HEIGHT / size.y);

  // Re-measure to rest the feet on the group origin (which we park at the deck or
  // the yard ground).
  const box = new THREE.Box3().setFromObject(model);
  const root = new THREE.Group();
  model.position.y = -box.min.y;
  root.add(model);

  model.traverse((o) => {
    if (o.isMesh) {
      // Foreground accent by the house: keep its shadows (the house casts too).
      o.castShadow = true;
      o.receiveShadow = true;
      o.raycast = () => {};   // never grabbed by the prune/shape raycast
      o.frustumCulled = false; // animated skinned mesh — its rest bounds under-cover the motion
      restoreCoat(o.material);
    }
  });

  const mixer = new THREE.AnimationMixer(model);
  const actions = {};
  for (const name of CLIPS) {
    const clip = THREE.AnimationClip.findByName(gltf.animations, name);
    if (clip) actions[name] = mixer.clipAction(clip);
  }

  pup = { root, mixer, actions, current: null, currentName: null, state: 'deckSit', timer: 0, target: null };
  shibaGroup.add(root);
  resetToDeck();
}

// Park the pup sitting on the deck, facing the garden (origin) — the start state,
// re-applied whenever the house is toggled back on so the pup greets you sitting.
function resetToDeck() {
  if (!pup) return;
  pup.root.position.set(SIT_SPOT.x, DECK_Y, SIT_SPOT.z);
  pup.root.rotation.y = Math.atan2(-SIT_SPOT.x, -SIT_SPOT.z) + FACING_YAW; // look toward origin
  enterDeckSit();
}

function buildShiba() {
  _building = true;
  loader.load(SHIBA_FILE, (gltf) => {
    _building = false;
    buildPup(gltf);
  }, undefined, (err) => { _building = false; console.error('[shiba] failed to load GLB', err); });
}

// Show/hide the pup in step with the house. First activation lazily builds it;
// later toggles flip group visibility and reset the pup back onto the deck.
export function setShibaActive(on) {
  const was = _active;
  _active = !!on;
  if (on && !shibaGroup) {
    shibaGroup = new THREE.Group();
    app.scene.add(shibaGroup);
  }
  if (on && !pup && !_building) buildShiba();
  else if (on && !was) resetToDeck(); // re-shown → greet sitting on the deck again
  if (shibaGroup) shibaGroup.visible = _active;
}

// Per-frame: advance the pup's mixer + behaviour. Cheap no-op while hidden.
export function updateShiba(dt) {
  if (!_active || !pup) return;
  tick(Math.min(dt, 0.05)); // clamp tab-switch hitches so it doesn't teleport
}
